import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, TextField, Stack, Chip, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFetch } from '../../../hooks/use-fetch';
import { BASE_ADMIN_API_URL } from '../../../config';
import { SESSION_TAGS } from '../../views/sessions/tags';

const RECORDING_DURATION = 60;
const INACTIVITY_TIMEOUT = 3000;

function bufferToCsv(buffer, sensors) {
  const sensorKeys = sensors.map((_, i) => `s${i}`);
  const header = ['timestamp', ...sensorKeys, 'event'].join(',');
  const rows = buffer.map(point => {
    const values = sensorKeys.map((_, i) => point.y[i] ?? '');
    return [point.x, ...values, point.event || ''].join(',');
  });
  return [header, ...rows].join('\n');
}

function countEvents(buffer) {
  const counts = {};
  buffer.forEach(point => {
    if (point.event) {
      counts[point.event] = (counts[point.event] || 0) + 1;
    }
  });
  return counts;
}

export default function useStreamRecorder({ emitter, deviceId, sensors = [], onComplete = () => {}, onAbort = () => {} }) {
  const [recording, setRecording] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(RECORDING_DURATION);
  const [eventFlash, setEventFlash] = useState(null);
  const [pendingBuffer, setPendingBuffer] = useState(null);
  const [pendingDuration, setPendingDuration] = useState(0);

  const bufferRef = useRef([]);
  const countdownRef = useRef(null);
  const inactivityRef = useRef(null);
  const listenerRef = useRef(null);
  const keyListenerRef = useRef(null);
  const markNextRef = useRef('');
  const recordingRef = useRef(false);
  const elapsedRef = useRef(0);
  const fetchWithAuth = useFetch();

  const cleanupListeners = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    if (inactivityRef.current) {
      clearTimeout(inactivityRef.current);
      inactivityRef.current = null;
    }
    if (listenerRef.current && emitter) {
      emitter.removeListener('data', listenerRef.current);
      listenerRef.current = null;
    }
    if (keyListenerRef.current) {
      document.removeEventListener('keydown', keyListenerRef.current);
      keyListenerRef.current = null;
    }
    markNextRef.current = '';
    recordingRef.current = false;
  }, [emitter]);

  const stopAndPrompt = useCallback((buffer, elapsed) => {
    cleanupListeners();
    setRecording(false);
    setSecondsLeft(RECORDING_DURATION);
    setPendingBuffer(buffer);
    setPendingDuration(elapsed);
  }, [cleanupListeners]);

  const upload = useCallback(async (buffer, duration, notes, tags) => {
    try {
      const csv = bufferToCsv(buffer, sensors);
      const events = countEvents(buffer);
      const sensorKeys = sensors.map((_, i) => `s${i}`);
      const body = {
        deviceId,
        csv,
        metadata: {
          duration,
          events,
          sensors: sensorKeys,
        },
      };
      if (notes) body.notes = notes;
      if (tags) body.tags = tags;
      const response = await fetchWithAuth(`${BASE_ADMIN_API_URL}/sessions/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        const session = await response.json();
        onComplete(session);
      }
    } catch (e) {
      console.error('Failed to upload recording', e);
    }
  }, [fetchWithAuth, deviceId, sensors, onComplete]);

  const confirmSave = useCallback((notes, tags) => {
    if (pendingBuffer) {
      upload(pendingBuffer, pendingDuration, notes, tags);
    }
    setPendingBuffer(null);
    setPendingDuration(0);
    bufferRef.current = [];
    elapsedRef.current = 0;
  }, [pendingBuffer, pendingDuration, upload]);

  const discardRecording = useCallback(() => {
    setPendingBuffer(null);
    setPendingDuration(0);
    bufferRef.current = [];
    elapsedRef.current = 0;
  }, []);

  const startRecording = useCallback(() => {
    if (recording || !emitter) return;

    bufferRef.current = [];
    markNextRef.current = '';
    recordingRef.current = true;
    elapsedRef.current = 0;
    setRecording(true);
    setSecondsLeft(RECORDING_DURATION);

    // Data listener
    const dataListener = (newData) => {
      if (!recordingRef.current) return;

      // Reset inactivity timeout
      if (inactivityRef.current) clearTimeout(inactivityRef.current);
      inactivityRef.current = setTimeout(() => {
        if (recordingRef.current) {
          cleanupListeners();
          setRecording(false);
          setSecondsLeft(RECORDING_DURATION);
          bufferRef.current = [];
          elapsedRef.current = 0;
          onAbort();
        }
      }, INACTIVITY_TIMEOUT);

      // Buffer the data points
      if (newData[0]) {
        const mark = markNextRef.current;
        markNextRef.current = '';
        newData[0].data.forEach(({ x }, i) => {
          const y = newData.map(sensor => {
            const point = sensor.data[i];
            return point ? point.y : null;
          });
          bufferRef.current.push({
            x,
            y,
            event: (mark && i === 0) ? mark : '',
          });
        });
      }
    };

    emitter.on('data', dataListener);
    listenerRef.current = dataListener;

    // Initial inactivity timeout
    inactivityRef.current = setTimeout(() => {
      if (recordingRef.current) {
        cleanupListeners();
        setRecording(false);
        setSecondsLeft(RECORDING_DURATION);
        bufferRef.current = [];
        elapsedRef.current = 0;
        onAbort();
      }
    }, INACTIVITY_TIMEOUT);

    // Keydown listener for event markers
    const keyListener = (e) => {
      if (e.key === 'm' || e.key === 'M') {
        markNextRef.current = 'move';
        setEventFlash('move');
        setTimeout(() => setEventFlash(null), 500);
      } else if (e.key === 'i' || e.key === 'I') {
        markNextRef.current = 'involuntary';
        setEventFlash('involuntary');
        setTimeout(() => setEventFlash(null), 500);
      }
    };
    document.addEventListener('keydown', keyListener);
    keyListenerRef.current = keyListener;

    // Countdown interval
    let remaining = RECORDING_DURATION;
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      elapsedRef.current += 1;
      setSecondsLeft(remaining);

      if (remaining <= 0) {
        stopAndPrompt([...bufferRef.current], elapsedRef.current);
      }
    }, 1000);
  }, [recording, emitter, cleanupListeners, stopAndPrompt, onAbort]);

  const stopRecording = useCallback(() => {
    if (!recordingRef.current) return;
    stopAndPrompt([...bufferRef.current], elapsedRef.current);
  }, [stopAndPrompt]);

  // Cleanup on unmount
  useEffect(() => {
    return () => cleanupListeners();
  }, [cleanupListeners]);

  return {
    recording,
    secondsLeft,
    eventFlash,
    pendingConfirm: pendingBuffer !== null,
    pendingDuration,
    startRecording,
    stopRecording,
    confirmSave,
    discardRecording,
  };
}

const StreamRecorderContext = createContext(null);

function ConfirmDialog({ recorder, t }) {
  const [notes, setNotes] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);

  const toggleTag = useCallback((value) => {
    setSelectedTags(prev => prev.includes(value) ? prev.filter(t => t !== value) : [...prev, value]);
  }, []);

  const handleSave = useCallback(() => {
    const tags = selectedTags.length > 0 ? selectedTags.join(',') : undefined;
    recorder.confirmSave(notes, tags);
    setNotes('');
    setSelectedTags([]);
  }, [recorder, notes, selectedTags]);

  const handleDiscard = useCallback(() => {
    recorder.discardRecording();
    setNotes('');
    setSelectedTags([]);
  }, [recorder]);

  return (
    <Dialog open={recorder.pendingConfirm} onClose={handleDiscard} fullWidth maxWidth="sm">
      <DialogTitle>{t('SESSIONS.CONFIRM_SAVE_TITLE')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography>
            {t('SESSIONS.CONFIRM_SAVE_BODY', { seconds: recorder.pendingDuration })}
          </Typography>
          <TextField
            multiline
            minRows={2}
            fullWidth
            label={t('SESSIONS.NOTES')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>{t('SESSIONS.TAGS')}</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {SESSION_TAGS.map(tag => (
                <Chip
                  key={tag.value}
                  label={t(tag.labelKey)}
                  variant={selectedTags.includes(tag.value) ? 'filled' : 'outlined'}
                  color={selectedTags.includes(tag.value) ? 'primary' : 'default'}
                  onClick={() => toggleTag(tag.value)}
                  size="small"
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDiscard}>{t('SESSIONS.DISCARD')}</Button>
        <Button variant="contained" onClick={handleSave}>{t('SESSIONS.SAVE')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export function StreamRecorderProvider({ emitter, deviceId, sensors, onComplete, onAbort, children }) {
  const { t } = useTranslation();
  const recorder = useStreamRecorder({ emitter, deviceId, sensors, onComplete, onAbort });

  return (
    <StreamRecorderContext.Provider value={recorder}>
      {children}
      <ConfirmDialog recorder={recorder} t={t} />
    </StreamRecorderContext.Provider>
  );
}

export function useStreamRecorderContext() {
  return useContext(StreamRecorderContext);
}
